import React, { useState, useEffect, useCallback } from "react";

//MUI components
import { CircularProgress, IconButton, Typography, InputAdornment, TextField, Menu, MenuItem } from "@material-ui/core";
import { DataGrid } from "@mui/x-data-grid";

//Icons
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

//Custom portal components
import { getTickets, updateTicket, getCampaignCatalogs, sendTicketResolvedNotification } from "../DataFunctions";
import CustomButton from "../common/CustomButton";
import TicketDetailModal from "./TicketDetailModal";

//External libraries
import Swal from "sweetalert2";

//styleSheets (se reutilizan los estilos de la tabla de contactos)
import "../CampaingPageComponents/Styles/contactsSection.css";

const localizedTextsMap = {
    // Vacío a propósito: el mensaje del estado vacío lo pinta TicketsSection
    // debajo de la tabla, porque depende de si hay tickets en otros estatus.
    noRowsLabel: "",
    columnHeaderSortIconLabel: "Ordenar columna",
};

// Formatea una fecha (ms, ISO o { $date }) a texto legible en es-MX.
const formatDate = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "—";
    let value = raw;
    if (typeof raw === "object") value = raw.$date ?? raw.date ?? null;
    if (value === null || value === undefined) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// Normaliza el estado del ticket a un valor canónico en mayúsculas.
const normalizeStatus = (raw) => {
    const s = (raw ?? "").toString().trim().toLowerCase();
    if (["abierto", "nuevo", "new", "open"].includes(s)) return "ABIERTO";
    if (["en_proceso", "en proceso", "inprogress", "in progress", "en progreso"].includes(s)) return "EN_PROCESO";
    if (["resuelto", "resolved"].includes(s)) return "RESUELTO";
    if (["cerrado", "closed"].includes(s)) return "CERRADO";
    return (raw ?? "").toString().toUpperCase();
};

// Color del estado del ticket (mismos tonos que la tabla de campañas):
//   ABIERTO -> azul, EN_PROCESO -> naranja, RESUELTO -> verde, CERRADO -> azul.
const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
        case "ABIERTO": return "#27315d";
        case "EN_PROCESO": return "#e48922";
        case "RESUELTO": return "#44AB3F";
        case "CERRADO": return "#27315d";
        default: return "#B2B2B2";
    }
};

// Etiqueta en español del estado del ticket.
const getStatusLabel = (status) => {
    switch (normalizeStatus(status)) {
        case "ABIERTO": return "Abierto";
        case "EN_PROCESO": return "En proceso";
        case "RESUELTO": return "Resuelto";
        case "CERRADO": return "Cerrado";
        default: return status || "—";
    }
};

// Normaliza un ticket del endpoint a las columnas de la tabla. Se mapea de
// forma tolerante porque el modelo del backend aún puede variar.
const normalizeTicket = (ticket, index) => ({
    id: ticket.id ?? ticket.ticketNumber ?? ticket.ticketId ?? ticket.ticket_id ?? `ticket-${index}`,
    // ticketId: identificador que espera el PATCH para actualizar el ticket.
    ticketId: ticket.ticketId ?? ticket.ticket_id ?? ticket.id ?? ticket.ticketNumber ?? "",
    folio: ticket.ticketNumber ?? ticket.folio ?? ticket.ticketId ?? ticket.ticket_id ?? "—",
    subject: ticket.subject ?? ticket.asunto ?? ticket.title ?? ticket.titulo ?? "—",
    status: ticket.status ?? ticket.estatus ?? ticket.estado ?? "—",
    assignedTo: ticket.assignedTo ?? ticket.assigned_to ?? ticket.agent ?? ticket.agente ?? "",
    priority: ticket.priority ?? ticket.prioridad ?? ticket.Ticket_Priority ?? "",
    tags: Array.isArray(ticket.tags)
        ? ticket.tags
        : Array.isArray(ticket.etiquetas)
        ? ticket.etiquetas
        : [],
    contact:
        ticket.contact ??
        ticket.contactName ??
        ticket.phone_number ??
        ticket.phone ??
        ticket.cliente ??
        "—",
    // Teléfono y nombre del cliente por separado (para notificar la resolución).
    phone: ticket.phone_number ?? ticket.phone ?? ticket.telefono ?? "",
    contactName: ticket.name ?? ticket.contactName ?? ticket.nombre ?? ticket.cliente ?? "",
    description:
        ticket.description ?? ticket.descripcion ?? ticket.message ?? ticket.mensaje ?? "—",
    createdAt: ticket.createdAt ?? ticket.created_at ?? ticket.fecha ?? ticket.date ?? null,
    // Conservamos el objeto crudo para mostrar el detalle completo en el modal.
    raw: ticket,
});

/**
 * Tabla con todos los tickets generados. Mismo estilo que la tabla de contactos.
 */
const TicketsSection = () => {
    const [tickets, setTickets] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorInFetch, setErrorInFetch] = useState(false);
    const [query, setQuery] = useState("");
    const [pageSize, setPageSize] = useState(10);

    // Modal de detalle del ticket seleccionado.
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    // Opciones de prioridad (catálogo Ticket_Priority de campañas).
    const [priorityOptions, setPriorityOptions] = useState([]);

    // Filtros por columna. Por defecto se muestran los tickets ABIERTOS.
    const [columnFilters, setColumnFilters] = useState({ status: "ABIERTO", priority: "" });

    // Control del menú desplegable de filtro: qué columna está abierta y su anchor.
    const [filterMenu, setFilterMenu] = useState({ field: null, anchorEl: null });

    const openFilterMenu = (field) => (event) => {
        event.stopPropagation();
        setFilterMenu({ field, anchorEl: event.currentTarget });
    };

    const closeFilterMenu = () => setFilterMenu({ field: null, anchorEl: null });

    const selectColumnFilter = (field, value) => {
        setColumnFilters((prev) => ({ ...prev, [field]: value }));
        closeFilterMenu();
    };

    const openTicketDetail = (ticket) => {
        setSelectedTicket(ticket);
        setDetailOpen(true);
    };

    const closeTicketDetail = () => {
        setDetailOpen(false);
        setSelectedTicket(null);
    };

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        setErrorInFetch(false);
        const res = await getTickets();

        if (res?.error || res?.response?.status >= 400 || res instanceof Error) {
            setTickets([]);
            setTotal(0);
            setErrorInFetch(true);
            setLoading(false);
            return;
        }

        // El endpoint devuelve: { response: { total, tickets: [...], pagination } }
        const rawData = res?.data;
        const list = Array.isArray(rawData)
            ? rawData
            : rawData?.response?.tickets ||
              rawData?.tickets ||
              rawData?.data ||
              rawData?.ticketList ||
              [];
        const totalCount =
            rawData?.response?.total ??
            rawData?.response?.pagination?.totalRecords ??
            rawData?.total ??
            (Array.isArray(list) ? list.length : 0);

        const normalized = list.map((ticket, index) => normalizeTicket(ticket, index));
        setTickets(normalized);
        setTotal(totalCount);
        setLoading(false);
    }, []);

    // Carga el catálogo Ticket_Priority de los catálogos de campañas.
    const fetchPriorityCatalog = useCallback(async () => {
        const res = await getCampaignCatalogs();
        if (res?.error || res instanceof Error) return;
        const catalogs = res?.data?.response?.catalogs || [];
        const priorityCatalog = catalogs.find((c) => c.name === "Ticket_Priority");
        const options =
            priorityCatalog?.catalog?.map((item) => ({
                value: item.value,
                label: item.descriptionES || item.value,
            })) || [];
        setPriorityOptions(options);
    }, []);

    useEffect(() => {
        fetchTickets();
        fetchPriorityCatalog();
    }, [fetchTickets, fetchPriorityCatalog]);

    // Opciones del filtro de estado (todas las transiciones + "Todos").
    const statusFilterOptions = [
        { value: "ABIERTO", label: "Abierto" },
        { value: "EN_PROCESO", label: "En proceso" },
        { value: "RESUELTO", label: "Resuelto" },
        { value: "CERRADO", label: "Cerrado" },
    ];

    const filteredTickets = tickets.filter((ticket) => {
        const search = query.toLowerCase();
        const folio = (ticket.folio || "").toString().toLowerCase();
        const subject = (ticket.subject || "").toString().toLowerCase();
        const contact = (ticket.contact || "").toString().toLowerCase();
        const matchesSearch = folio.includes(search) || subject.includes(search) || contact.includes(search);

        // Filtro por estado (normalizado).
        const statusFilter = columnFilters.status;
        const matchesStatus = !statusFilter || normalizeStatus(ticket.status) === statusFilter;

        // Filtro por prioridad.
        const priorityFilter = columnFilters.priority;
        const matchesPriority = !priorityFilter || (ticket.priority || "") === priorityFilter;

        return matchesSearch && matchesStatus && matchesPriority;
    });

    // Opciones por campo para poblar el menú de filtro según la columna abierta.
    const filterOptionsByField = {
        status: statusFilterOptions,
        priority: priorityOptions,
    };

    // ---- Estado vacío: distingue "no hay nada" de "no hay con estos filtros" ----
    // Total real de tickets (el endpoint informa el total; nunca menos de lo cargado).
    const allCount = Math.max(total, tickets.length);
    const canShowEmpty = !loading && !errorInFetch;
    // Caso 1: no existe ningún ticket.
    const hasNoTicketsAtAll = canShowEmpty && tickets.length === 0;
    // Caso 2: hay tickets, pero los filtros/búsqueda los ocultan todos.
    const isFilteredEmpty = canShowEmpty && tickets.length > 0 && filteredTickets.length === 0;

    // Descripción legible de lo que está filtrando (para el mensaje del caso 2).
    const activeFilterText = (() => {
        const parts = [];
        if (columnFilters.status) {
            const opt = statusFilterOptions.find((o) => o.value === columnFilters.status);
            parts.push(`estatus "${opt?.label || columnFilters.status}"`);
        }
        if (columnFilters.priority) {
            const opt = priorityOptions.find((o) => o.value === columnFilters.priority);
            parts.push(`prioridad "${opt?.label || columnFilters.priority}"`);
        }
        if (query.trim()) parts.push(`la búsqueda "${query.trim()}"`);
        return parts.join(" y ");
    })();

    // Quita estatus, prioridad y búsqueda para ver todos los tickets.
    const clearAllFilters = () => {
        setColumnFilters({ status: "", priority: "" });
        setQuery("");
    };

    // Guarda asignación y/o estado del ticket. Solo llama al endpoint que
    // corresponda según lo que haya cambiado respecto al ticket original.
    // El modal construye el payload según la transición (incluido ticketId).
    // Aquí solo se envía al backend y se maneja el resultado.
    const handleSaveTicket = async (payload) => {
        // Modal de carga mientras se procesa (se cierra al mostrar el resultado).
        Swal.fire({
            title: "Guardando cambios...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
        });

        // Las notas al asignar van en una petición aparte (el backend ignora
        // tags cuando se manda status). Las separamos del payload de transición.
        const { extraTags, ...transitionPayload } = payload;

        const res = await updateTicket(transitionPayload);

        if (res?.error || res?.response?.status >= 400 || res instanceof Error) {
            // 409 = transición no permitida por el backend.
            const status = res?.response?.status;
            const backendMsg = res?.response?.data?.response?.message || res?.response?.data?.message;
            Swal.fire({
                icon: "error",
                title: status === 409 ? "Transición no permitida" : "No se pudo actualizar el ticket",
                text: backendMsg || "Intenta nuevamente más tarde.",
            });
            return false;
        }

        // Si al asignar se capturaron notas, se guardan en un PATCH aparte
        // (sin status), ya que el backend las ignora junto a la transición.
        if (Array.isArray(extraTags) && extraTags.length > 0) {
            Swal.update({ title: "Guardando notas..." });
            Swal.showLoading();
            await updateTicket({
                ticketId: transitionPayload.ticketId,
                tags: extraTags,
            });
        }

        // Si el ticket se marcó como RESUELTO, se notifica al cliente por WhatsApp.
        if (payload.status === "RESUELTO" && selectedTicket) {
            // Actualiza el modal de carga para el paso de notificación.
            Swal.update({ title: "Notificando al cliente..." });
            Swal.showLoading();
            const notifyRes = await sendTicketResolvedNotification({
                phone: selectedTicket.phone || selectedTicket.contact,
                name: selectedTicket.contactName || selectedTicket.contact,
                folio: selectedTicket.folio,
            });
            // Si la notificación falla, no bloqueamos el flujo: el ticket ya se
            // actualizó. Solo se avisa que no se pudo notificar al cliente.
            if (notifyRes?.error || notifyRes?.response?.status >= 400 || notifyRes instanceof Error) {
                await fetchTickets();
                Swal.fire({
                    icon: "warning",
                    title: "Ticket resuelto",
                    text: "El ticket se actualizó, pero no se pudo enviar la notificación al cliente.",
                });
                return true;
            }
        }

        await fetchTickets();
        Swal.fire({
            icon: "success",
            title: "Ticket actualizado",
            timer: 1800,
            showConfirmButton: false,
        });
        return true;
    };

    // Header de columna con indicador de filtro desplegable (chevron ▾),
    // igual que en la tabla de contactos.
    const renderFilterHeader = (title, field) => {
        const active = columnFilters[field];
        return (
            <div className="contacts-filter-header" onClick={openFilterMenu(field)}>
                <span style={{ fontWeight: 600 }}>{title}</span>
                <ExpandMoreIcon
                    fontSize="small"
                    className={active ? "contacts-filter-chevron-active" : ""}
                    style={{ marginLeft: 2 }}
                />
            </div>
        );
    };

    const columns = [
        {
            field: "folio",
            headerName: "Folio",
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => (
                <span
                    className="contacts-name-link"
                    onClick={() => openTicketDetail(params.row)}
                >
                    {params.row.folio || "—"}
                </span>
            ),
        },
        {
            field: "subject",
            headerName: "Asunto",
            flex: 1.2,
            minWidth: 170,
            renderCell: (params) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4, width: "100%" }}>
                    {params.row.subject || "—"}
                </div>
            ),
        },
        {
            field: "description",
            headerName: "Descripción",
            flex: 2.6,
            minWidth: 280,
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.4,
                        width: "100%",
                    }}
                >
                    {params.row.description || "—"}
                </div>
            ),
        },
        {
            field: "status",
            headerName: "Estatus",
            flex: 1,
            minWidth: 140,
            sortable: false,
            renderHeader: () => renderFilterHeader("Estatus", "status"),
            renderCell: (params) => (
                <span style={{ color: getStatusColor(params.row.status), fontWeight: 500 }}>
                    {getStatusLabel(params.row.status)}
                </span>
            ),
        },
        {
            field: "priority",
            headerName: "Prioridad",
            flex: 1,
            minWidth: 140,
            sortable: false,
            renderHeader: () => renderFilterHeader("Prioridad", "priority"),
            renderCell: (params) => {
                // Muestra la etiqueta del catálogo si existe, o el valor crudo.
                const opt = priorityOptions.find((o) => o.value === params.row.priority);
                return <span>{opt?.label || params.row.priority || "—"}</span>;
            },
        },
        {
            field: "createdAt",
            headerName: "Fecha de creación",
            flex: 1.2,
            minWidth: 170,
            renderCell: (params) => <span>{formatDate(params.row.createdAt)}</span>,
        },
    ];

    return (
        <div className="contacts-section">
            {/* Búsqueda */}
            <div className="contacts-search-row">
                <Typography variant="body2" style={{ color: "#6b6b6b", marginBottom: 6 }}>
                    Buscar por folio
                </Typography>
                <TextField
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Ej. 4266814225"
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{ color: "#9a9a9a" }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            {/* Botón de actualizar */}
            <div className="contacts-actions-row">
                <div className="contacts-actions-right" style={{ marginLeft: "auto" }}>
                    <IconButton title="Actualizar" onClick={fetchTickets}>
                        <RefreshIcon color="primary" />
                    </IconButton>
                </div>
            </div>

            {/* Listado */}
            <div className="contacts-list-card">
                <Typography variant="subtitle1" style={{ color: "#27315D", fontWeight: 600, marginBottom: 8 }}>
                    Listado de tickets
                </Typography>

                {loading ? (
                    <div className="center-loading-icon">
                        <CircularProgress />
                    </div>
                ) : errorInFetch ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <span>Hubo un error al cargar los tickets, intenta más tarde.</span>
                        <IconButton onClick={() => fetchTickets()}>
                            <RefreshIcon color="primary" width="20px" height="20px" />
                        </IconButton>
                    </div>
                ) : (
                    <div className="contacts-datagrid">
                        <DataGrid
                            disableColumnMenu
                            disableSelectionOnClick
                            rows={filteredTickets}
                            columns={columns}
                            localeText={localizedTextsMap}
                            autoHeight
                            getRowHeight={() => "auto"}
                            pageSize={pageSize}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    </div>
                )}

                {/* Estado vacío. Se distingue entre "no hay ningún ticket" y
                    "hay tickets, pero no con los filtros aplicados". */}
                {hasNoTicketsAtAll && (
                    <div className="tickets-empty-state">
                        <Typography className="tickets-empty-title">Aún no hay tickets registrados</Typography>
                        <Typography variant="body2">Cuando se genere un ticket aparecerá aquí.</Typography>
                    </div>
                )}
                {isFilteredEmpty && (
                    <div className="tickets-empty-state">
                        <Typography className="tickets-empty-title">
                            No hay tickets con {activeFilterText}
                        </Typography>
                        <Typography variant="body2">
                            Hay {allCount.toLocaleString()} {allCount === 1 ? "ticket" : "tickets"} en total, pero
                            no coinciden con los filtros aplicados.
                        </Typography>
                        <div className="tickets-empty-action">
                            <CustomButton onClick={clearAllFilters}>Ver todos los tickets</CustomButton>
                        </div>
                    </div>
                )}

                {/* Cuántos tickets se están mostrando del total */}
                {canShowEmpty && (
                    <div className="contacts-total-footer">
                        <Typography variant="body2" style={{ color: "#6b6b6b" }}>
                            Mostrando <strong style={{ color: "#27315D" }}>{filteredTickets.length.toLocaleString()}</strong>{" "}
                            de <strong style={{ color: "#27315D" }}>{allCount.toLocaleString()}</strong>{" "}
                            {allCount === 1 ? "ticket" : "tickets"}
                        </Typography>
                    </div>
                )}
            </div>

            {/* Modal de detalle del ticket (asignar / cambiar estado) */}
            <TicketDetailModal
                open={detailOpen}
                ticket={selectedTicket}
                priorityOptions={priorityOptions}
                onBack={closeTicketDetail}
                onSave={handleSaveTicket}
            />

            {/* Menú desplegable de filtro por columna (Estatus / Prioridad) */}
            <Menu
                anchorEl={filterMenu.anchorEl}
                open={Boolean(filterMenu.anchorEl)}
                onClose={closeFilterMenu}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <MenuItem
                    selected={!columnFilters[filterMenu.field]}
                    onClick={() => selectColumnFilter(filterMenu.field, "")}
                >
                    <em>Todos</em>
                </MenuItem>
                {(filterOptionsByField[filterMenu.field] || []).map((option) => (
                    <MenuItem
                        key={option.value}
                        selected={columnFilters[filterMenu.field] === option.value}
                        onClick={() => selectColumnFilter(filterMenu.field, option.value)}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default TicketsSection;
